import React, {useCallback, useMemo, useState} from 'react';
import {Dropdown} from 'react-bootstrap';
import {Icon} from 'shared/helpers';
import OutsideClickHandler from 'shared/components/Header/OutsideClickHandler';
import type {TableRowType, TableButton, TableDropdown} from 'shared/types';

type DropdownOptionType = {
    row: TableRowType;
    button: TableButton;
    isSmallDevice?: boolean;
    hasTitle?: boolean;
};

const DropdownOption = ({row, button, hasTitle = true}: DropdownOptionType) => {
    const [show, setShow] = useState<boolean>(false);
    const onOutsideClickFunc = useCallback(() => {
        setShow(false);
    }, []);

    const toggleDropdown = useCallback(() => {
        setShow((prev) => !prev);
    }, []);

    const onItemClick = useCallback(
        (option: TableDropdown) => (event: React.MouseEvent<HTMLElement>) => {
            if (typeof option.action === 'function') {
                option.action();
            } else {
                button.action(row, event, option.name);
            }
        },
        [row]
    );

    const options = useMemo(() => {
        const {dynamicOptions, staticOptions} = button.dropdown.reduce(
            (acc, option) => {
                if (option.source) {
                    acc.dynamicOptions.push(option);
                } else {
                    acc.staticOptions.push(option);
                }
                return acc;
            },
            {
                dynamicOptions: [] as TableDropdown[],
                staticOptions: [] as TableDropdown[],
            }
        );

        const allOptions = [...staticOptions];
        dynamicOptions.forEach((option) => {
            const fieldOptions = row[option.source] as unknown as {
                [key: string]: string | number;
            }[];
            fieldOptions
                ?.filter((fieldOption) => {
                    if (option.type && option.type !== fieldOption.type) {
                        // if filtering by type, include only the type selected
                        return false;
                    }
                    return true;
                })
                .forEach((fieldOption) => {
                    const newOption: TableDropdown = {
                        name: String(fieldOption.name),
                        show: option.show,
                        order: option.order,
                    };
                    if (typeof option.action === 'function') {
                        newOption.action = () =>
                            option.action(
                                Number(
                                    fieldOption[
                                        option.identifier as keyof typeof fieldOption
                                    ]
                                )
                            );
                    }
                    allOptions.push(newOption);
                });
        });

        return allOptions.sort((current, next) => current.order - next.order);
    }, [button]);

    return (
        <OutsideClickHandler onOutsideClick={onOutsideClickFunc}>
            <Dropdown
                key={row?.id}
                className="table-option-dropdown"
                drop="end"
                show={show}
                onToggle={toggleDropdown}>
                <Dropdown.Toggle
                    variant="link"
                    title={hasTitle ? button.title : ''}
                    role="table-option">
                    <Icon iconName={button.iconName} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {options.map((option, index) => {
                        if (option.hasOwnProperty('show')) {
                            if (
                                typeof option.show === 'boolean' &&
                                !option.show
                            )
                                return <div key={index} />;

                            if (
                                typeof option.show === 'function' &&
                                !option.show(row)
                            )
                                return <div key={index} />;
                        }

                        return (
                            <Dropdown.Item
                                key={index}
                                onClick={onItemClick(option)}
                                role="table-option-dropdown-menu">
                                {option.name}
                            </Dropdown.Item>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown>
        </OutsideClickHandler>
    );
};

export default DropdownOption;
